import { AutoComplete, Button as AntButton, Select as AntSelect } from "antd";
import classNames from "classnames";
import { get, isEmpty, keyBy } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { MdClose, MdPlayArrow } from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import { SIMULATION_CHOICES, SIMULATION_STOPS } from "./journey-simulation";
import { EXIT_TYPES, exitType, splitDelay } from "./next-steps";

const ACTION_BY_STOP = {
  [SIMULATION_STOPS.SHOWN]: "CONTINUE_SHOWN",
  [SIMULATION_STOPS.WAITING]: "CONTINUE_WAITING",
};

const SimulationPanel = ({
  steps,
  fields,
  values,
  result,
  canGoBack,
  onValueChange,
  onChoose,
  onBack,
  onRestart,
  onSelectStep,
  onClose,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const SIMULATION = t("CANVAS_EDITOR.SIMULATION", { returnObjects: true });
  const CONDITIONS = t("FORM.POSSIBLE_STATUSES.CONDITIONS", {
    returnObjects: true,
  });
  const WAITS_FOR = t("CANVAS_EDITOR.STEP.WAIT.WAITS_FOR", {
    returnObjects: true,
  });
  const SHORT_UNITS = t("CANVAS_EDITOR.STEPS.SHORT_UNITS", {
    returnObjects: true,
  });
  const UNNAMED = t("CANVAS_EDITOR.STEPS.UNNAMED");

  const stepById = keyBy(steps, "id");
  const nameOf = (id) => get(stepById, [id, "data", "name"]) || UNNAMED;
  const { stop } = result;
  const stopStep = stepById[stop.stepId];

  const stopText = () => {
    if (stop.type === SIMULATION_STOPS.WAITING)
      return (
        get(WAITS_FOR, get(stopStep, "data.nodeType")) || SIMULATION.WAITING
      );
    return SIMULATION.STOPS[stop.type];
  };

  const exitLabel = (edge) => {
    const type = exitType(edge);
    const target = nameOf(edge.target);
    if (type === EXIT_TYPES.BACK)
      return t("CANVAS_EDITOR.SIMULATION.EXITS.BACK", { target });
    if (type === EXIT_TYPES.EVENT)
      return t("CANVAS_EDITOR.SIMULATION.EXITS.EVENT", {
        event: get(edge, "data.event") || "?",
        target,
      });
    const delay = splitDelay(get(edge, "data.delaySeconds"));
    return t("CANVAS_EDITOR.SIMULATION.EXITS.TIMER", {
      delay: `${delay.value} ${SHORT_UNITS[delay.unit]}`,
      target,
    });
  };

  const fieldInput = (field) => {
    const value = values[field.accessor];
    const change = (next) => onValueChange(field.accessor, next);
    const shared = {
      allowClear: true,
      className: "canvas-simulation__input",
      "aria-label": field.label,
      placeholder: SIMULATION.EMPTY_VALUE,
    };
    if (!isEmpty(field.options))
      return (
        <AntSelect
          {...shared}
          virtual={false}
          options={field.options}
          value={value ?? undefined}
          onChange={change}
        />
      );
    if (field.boolean)
      return (
        <AntSelect
          {...shared}
          options={[
            { value: true, label: CONDITIONS.YES },
            { value: false, label: CONDITIONS.NO },
          ]}
          value={value ?? undefined}
          onChange={change}
        />
      );
    return (
      <AutoComplete
        {...shared}
        options={field.suggestions.map((suggestion) => ({
          value: suggestion,
        }))}
        value={value ?? ""}
        onChange={change}
      />
    );
  };

  const canContinue = Boolean(ACTION_BY_STOP[stop.type]);
  const canFollowExits = [
    SIMULATION_STOPS.SHOWN,
    SIMULATION_STOPS.WAITING,
    SIMULATION_STOPS.PARKED,
  ].includes(stop.type);

  return (
    <div
      className="canvas-simulation"
      role="dialog"
      aria-label={SIMULATION.TITLE}
    >
      <div className="canvas-checks__header">
        <div>
          <h2 className="canvas-checks__title">{SIMULATION.TITLE}</h2>
          <p className="canvas-checks__summary">{SIMULATION.HINT}</p>
        </div>
        <button
          type="button"
          className="canvas-checks__close"
          aria-label={SIMULATION.CLOSE}
          onClick={onClose}
        >
          <MdClose size={20} />
        </button>
      </div>

      <div className="canvas-simulation__body">
        <section className="canvas-simulation__section">
          <h3 className="canvas-simulation__subtitle">{SIMULATION.ANSWERS}</h3>
          {fields.length === 0 ? (
            <p className="canvas-simulation__muted">{SIMULATION.NO_FIELDS}</p>
          ) : null}
          {fields.map((field) => (
            <div key={field.accessor} className="canvas-simulation__field">
              <span className="canvas-simulation__label">{field.label}</span>
              {fieldInput(field)}
            </div>
          ))}
        </section>

        <section className="canvas-simulation__section">
          <h3 className="canvas-simulation__subtitle">{SIMULATION.PATH}</h3>
          <ol className="canvas-simulation__path">
            {result.steps.map((id, index) => (
              <li key={`${id}-${index}`}>
                <button
                  type="button"
                  className={classNames("canvas-simulation__step", {
                    "canvas-simulation__step--current":
                      index === result.steps.length - 1,
                  })}
                  onClick={() => onSelectStep(id)}
                >
                  <span className="canvas-simulation__number">{index + 1}</span>
                  {nameOf(id)}
                </button>
              </li>
            ))}
          </ol>
          <div
            className={`canvas-simulation__stop canvas-simulation__stop--${stop.type.toLowerCase()}`}
          >
            <strong>{stopText()}</strong>
            {canContinue || (canFollowExits && stop.exits.length) ? (
              <div className="canvas-simulation__actions">
                {canContinue ? (
                  <AntButton
                    type="primary"
                    size="small"
                    icon={<MdPlayArrow size={16} />}
                    onClick={() =>
                      onChoose({ type: SIMULATION_CHOICES.CONTINUE })
                    }
                  >
                    {SIMULATION.ACTIONS[ACTION_BY_STOP[stop.type]]}
                  </AntButton>
                ) : null}
                {canFollowExits
                  ? stop.exits.map((edge) => (
                      <AntButton
                        key={edge.id}
                        size="small"
                        onClick={() =>
                          onChoose({
                            type: SIMULATION_CHOICES.EXIT,
                            edgeId: edge.id,
                          })
                        }
                      >
                        {exitLabel(edge)}
                      </AntButton>
                    ))
                  : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <div className="canvas-simulation__footer">
        <AntButton size="small" onClick={onBack} disabled={!canGoBack}>
          {SIMULATION.BACK}
        </AntButton>
        <AntButton size="small" onClick={onRestart}>
          {SIMULATION.RESTART}
        </AntButton>
        <span className="canvas-simulation__muted">{SIMULATION.NOTE}</span>
      </div>
    </div>
  );
};

SimulationPanel.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(PropTypes.object),
  values: PropTypes.object,
  result: PropTypes.object.isRequired,
  canGoBack: PropTypes.bool,
  onValueChange: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onRestart: PropTypes.func.isRequired,
  onSelectStep: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SimulationPanel.defaultProps = {
  steps: [],
  fields: [],
  values: {},
  canGoBack: false,
};

export default SimulationPanel;
